import { TypedDocumentNode, gql } from '@apollo/client';
import { faHourglass, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { findLastIndex, sortBy } from '@sorare/core/src/lib/arrays';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { TargetIcon } from 'assets/icons/TargetIcon';

import { targetColors } from '../ThresholdRewards';
import { Cursor } from './Cursor';
import {
  ThresholdProgressBar_so5Leaderboard,
  ThresholdProgressBar_so5Lineup,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 1.5 })`
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  backdrop-filter: blur(32px);
`;
const Bar = styled.div`
  border-radius: var(--unit);
  background-color: var(--c-nd-200);
  position: relative;
`;
const Sections = styled(Horizontal).attrs({ gap: 0 })`
  position: relative;
`;

const Section = styled.div`
  position: relative;
  height: calc(5 * var(--unit));
  display: inline-flex;
  justify-content: flex-start;
  align-items: flex-end;
  padding: var(--unit);
  color: var(--c-nl-500);

  & > * {
    z-index: 1;
  }

  &::before {
    inset: 0;
    content: '';
    opacity: 0.5;
    position: absolute;
  }

  &:first-of-type::before {
    border-radius: var(--unit) 0 0 var(--unit);
  }
  &:last-of-type::before {
    border-radius: 0 var(--unit) var(--unit) 0;
  }

  &.missed::before {
    background-color: var(--c-nd-100);
  }

  &.target1::before {
    background-color: var(--c-score-medium);
  }

  &.target2::before {
    background-color: var(--c-score-mediumHigh);
  }

  &.target3::before {
    background-color: var(--c-score-high);
  }
`;

const StyledCursor = styled(Cursor)`
  z-index: 1;
  position: absolute;
  top: calc(-1 * var(--intermediate-unit));
  transform: translateX(50%);
`;

type Props = {
  so5Leaderboard: ThresholdProgressBar_so5Leaderboard;
  so5Lineup?: Nullable<ThresholdProgressBar_so5Lineup>;
};

export const ThresholdProgressBar = ({ so5Leaderboard, so5Lineup }: Props) => {
  const { so5Rankings, rewardsConfig } = so5Leaderboard;

  if (!so5Rankings) {
    return null;
  }

  const so5Ranking = so5Lineup?.so5Rankings[0];
  const hasUpcomingGame = so5Lineup?.so5Appearances.some(
    appearance => !!appearance.upcomingGame
  );

  const thresholds = sortBy(
    ({ score }) => score || 0,
    (rewardsConfig.conditional || []).filter(Boolean)
  );
  const initialThresholdIndex =
    thresholds
      ?.slice()
      .reverse()
      .findIndex(
        reward =>
          reward?.score && so5Ranking?.score && reward.score <= so5Ranking.score
      ) ?? 0;
  const currentThresholdIndex = findLastIndex(
    thresholds || [],
    t => (so5Ranking?.score ?? 0) >= (t?.score ?? 0)
  );

  const sectionsWidth = 100 / (thresholds.length + 1);

  const getCursorPosition = () => {
    if (!so5Ranking?.score || !thresholds.length) {
      return 0;
    }

    const currentScore = so5Ranking.score;

    // Find the threshold just below and above the current score

    const currentThreshold =
      currentThresholdIndex >= 0
        ? thresholds[currentThresholdIndex]
        : undefined;
    const nextThreshold = thresholds[currentThresholdIndex + 1];

    const sectionIndex = currentThreshold
      ? thresholds.indexOf(currentThreshold) + 1
      : 0;
    const startScore = currentThreshold?.score ?? 0;
    const endScore = nextThreshold?.score ?? so5Rankings.nodes[0].score;

    // Calculate base position for the section
    const basePosition = sectionIndex * sectionsWidth;

    // Calculate position within the section
    const scoreRange = endScore - startScore;
    const scoreProgress = currentScore - startScore;
    const sectionProgress = scoreRange > 0 ? scoreProgress / scoreRange : 0;

    return `calc(100% - ${basePosition + sectionProgress * sectionsWidth}%)`;
  };

  return (
    <Root>
      {so5Lineup && (
        <SBHorizontal>
          {currentThresholdIndex >= 0 ? (
            <Horizontal>
              <TargetIcon color={targetColors[initialThresholdIndex]} />
              <LabelL bold>
                <FormattedMessage
                  {...(thresholds.length === 1
                    ? fantasy.target
                    : fantasy.targetNumber)}
                  values={{
                    targetNb: initialThresholdIndex + 1,
                  }}
                />
              </LabelL>
            </Horizontal>
          ) : (
            <Horizontal>
              {hasUpcomingGame ? (
                <FontAwesomeIcon icon={faHourglass} />
              ) : (
                <FontAwesomeIcon icon={faTimes} color="var(--c-red-600)" />
              )}
              <LabelL bold>
                {hasUpcomingGame ? (
                  <FormattedMessage
                    id="ThresholdProgressBar.inProgress"
                    defaultMessage="In Progress"
                  />
                ) : (
                  <FormattedMessage
                    id="ThresholdProgressBar.targetMissed"
                    defaultMessage="Target Missed"
                  />
                )}
              </LabelL>
            </Horizontal>
          )}
          <LabelL bold>
            <FormattedMessage
              {...fantasy.pointsWithoutFormat}
              values={{ score: so5Ranking?.score ?? 0 }}
            />
          </LabelL>
        </SBHorizontal>
      )}
      <Bar>
        <Sections>
          <Section className="missed" style={{ width: `${sectionsWidth}%` }}>
            <LabelS as="p" bold>
              <FormattedNumber value={0} maximumFractionDigits={0} />
            </LabelS>
          </Section>
          {thresholds?.map((threshold, index) => (
            <Section
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={`target${index + 1}`}
              style={{ width: `${sectionsWidth}%` }}
            >
              <LabelS as="p" bold>
                <FormattedNumber
                  value={threshold.score ?? 0}
                  maximumFractionDigits={0}
                />
              </LabelS>
            </Section>
          ))}
        </Sections>
        {(so5Ranking?.score || 0) > 0 && (
          <StyledCursor
            stroke={false}
            color="rgb(var(--c-rgb-white))"
            style={{
              right: getCursorPosition() || '100%',
            }}
          />
        )}
      </Bar>
      {currentThresholdIndex + 1 < (thresholds?.length ?? 0) && (
        <LabelM color="var(--c-nd-600)" className="text-center">
          <FormattedMessage
            id="LineupStatus.nextThreshold"
            defaultMessage="<span>{points, number}pts</span> to next target"
            values={{
              points: Math.ceil(
                (thresholds?.[(currentThresholdIndex ?? 0) + 1]?.score ?? 0) -
                  (so5Ranking?.score ?? 0)
              ),
              span: (...chunks) => (
                <span
                  style={{
                    color: 'var(--c-nl-100-opaque)',
                    fontWeight: 'bold',
                  }}
                >
                  {chunks}
                </span>
              ),
            }}
          />
        </LabelM>
      )}
    </Root>
  );
};

ThresholdProgressBar.fragments = {
  so5Leaderboard: gql`
    fragment ThresholdProgressBar_so5Leaderboard on So5Leaderboard {
      slug
      so5Rankings(first: 1) {
        nodes {
          id
          score
        }
      }
      so5LineupsCount
      so5Fixture {
        slug
        aasmState
      }
      rewardsConfig {
        conditional {
          score
        }
      }
    }
  ` as TypedDocumentNode<ThresholdProgressBar_so5Leaderboard>,
  so5Lineup: gql`
    fragment ThresholdProgressBar_so5Lineup on So5Lineup {
      id
      so5Appearances {
        id
        upcomingGame {
          id
        }
      }
      so5Rankings {
        id
        score
        ranking
      }
    }
  ` as TypedDocumentNode<ThresholdProgressBar_so5Lineup>,
};
